import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, Trash, PencilSimple } from "@phosphor-icons/react";

const empty = {
  full_name: "", email: "", phone: "", nationality: "",
  date_of_birth: "", passport_number: "", address: "", notes: "",
};

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/clients");
    setClients(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (editing) await api.put(`/clients/${editing}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Client updated" : "Client created");
      setOpen(false); setForm(empty); setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client? Related matters will remain but reference will be lost.")) return;
    try { await api.delete(`/clients/${id}`); toast.success("Client deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const edit = (c) => {
    setEditing(c.id);
    setForm({ ...empty, ...Object.fromEntries(Object.entries(c).filter(([, v]) => v != null)) });
    setOpen(true);
  };

  const filtered = clients.filter((c) => {
    const s = q.toLowerCase();
    return !s || [c.full_name, c.email, c.nationality, c.passport_number].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Directory</div>
          <h2 className="font-serif-brand text-3xl">Clients</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="clients-search"
              placeholder="Search clients…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8 rounded-sm h-9 w-64 border-slate-300"
            />
          </div>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-client-button" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B] h-9">
                <Plus size={14} className="mr-1" /> New Client
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Client" : "New Client"}</DialogTitle></DialogHeader>
              <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full name *" required value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="client-name" />
                <Field label="Nationality" value={form.nationality} onChange={(v) => setForm({ ...form, nationality: v })} />
                <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                <Field label="Date of birth" type="date" value={form.date_of_birth || ""} onChange={(v) => setForm({ ...form, date_of_birth: v })} />
                <Field label="Passport number" value={form.passport_number} onChange={(v) => setForm({ ...form, passport_number: v })} />
                <div className="md:col-span-2">
                  <Label className="text-xs tracking-wider uppercase text-slate-500">Address</Label>
                  <Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 rounded-sm" rows={2} />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs tracking-wider uppercase text-slate-500">Notes</Label>
                  <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm" rows={3} />
                </div>
                <DialogFooter className="md:col-span-2">
                  <Button type="submit" data-testid="save-client" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B]">
                    {editing ? "Save changes" : "Create client"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs tracking-wider uppercase text-slate-500">
            <tr>
              <th className="text-left px-6 py-3 font-medium">Client</th>
              <th className="text-left px-6 py-3 font-medium">Nationality</th>
              <th className="text-left px-6 py-3 font-medium">Contact</th>
              <th className="text-left px-6 py-3 font-medium">Added</th>
              <th className="text-right px-6 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filtered.length === 0 && (
              <tr><td colSpan="5" className="px-6 py-12 text-center text-slate-500">No clients yet.</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`client-row-${c.id}`} className="hover:bg-slate-50">
                <td className="px-6 py-3">
                  <div className="font-medium">{c.full_name}</div>
                  {c.passport_number && <div className="text-xs text-slate-500 font-mono-num">Passport {c.passport_number}</div>}
                </td>
                <td className="px-6 py-3 text-slate-600">{c.nationality || "—"}</td>
                <td className="px-6 py-3 text-slate-600">
                  <div>{c.email || "—"}</div>
                  <div className="text-xs text-slate-500">{c.phone || ""}</div>
                </td>
                <td className="px-6 py-3 text-slate-600">{formatDate(c.created_at)}</td>
                <td className="px-6 py-3 text-right">
                  <button data-testid={`edit-client-${c.id}`} onClick={() => edit(c)} className="text-slate-500 hover:text-[#0B1528] mr-3">
                    <PencilSimple size={16} />
                  </button>
                  <button data-testid={`delete-client-${c.id}`} onClick={() => remove(c.id)} className="text-slate-500 hover:text-red-700">
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <Label className="text-xs tracking-wider uppercase text-slate-500">{label}</Label>
      <Input data-testid={testid} type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 rounded-sm" />
    </div>
  );
}
